"use client";

import { CheckCircle2, Loader2 } from "lucide-react";
import { PaymentElement, useCheckout } from "@stripe/react-stripe-js/checkout";
import React, { FormEvent, useEffect, useState } from "react";

import { CheckoutFormInnerProps } from "./types";
import { useCheckoutServerSync } from "./useCheckoutServerSync";

export function CheckoutFormInner({
  formId,
  onPaymentComplete,
  onSubmittingChange,
  onReady,
  onLoadError,
  userEmail,
  successMessage,
  onBeforeConfirm,
  serverUpdate,
}: CheckoutFormInnerProps) {
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [paymentSuccess, setPaymentSuccess] = useState(false);
  const checkoutState = useCheckout();

  const sessionFailed = checkoutState.type === "error";
  useEffect(() => {
    if (sessionFailed) onLoadError();
  }, [sessionFailed, onLoadError, onReady]);

  const serverSync = useCheckoutServerSync(
    checkoutState.type === "success" ? checkoutState.checkout : null,
    serverUpdate,
    onLoadError
  );

  if (checkoutState.type === "loading") {
    return (
      <div className="flex items-center justify-center py-4">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
        <span className="ml-2 text-sm text-muted-foreground">
          Loading payment form...
        </span>
      </div>
    );
  }

  if (checkoutState.type === "error") {
    return (
      <div className="py-4 text-sm text-red-500">
        {checkoutState.error.message}
      </div>
    );
  }

  const { checkout } = checkoutState;

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    onSubmittingChange(true);
    setErrorMessage(null);

    try {
      if (!(await serverSync.settled())) {
        onSubmittingChange(false);
        return;
      }
      await onBeforeConfirm?.();
      const result = await checkout.confirm({
        redirect: "if_required",
      });

      if (result.type === "error") {
        setErrorMessage(result.error.message);
        onSubmittingChange(false);
        return;
      }

      setPaymentSuccess(true);
      onSubmittingChange(false);
      onPaymentComplete();
    } catch (err) {
      setErrorMessage(
        err instanceof Error
          ? err.message
          : "Something went wrong with your payment. Please try again."
      );
      onSubmittingChange(false);
    }
  };

  if (paymentSuccess) {
    return (
      <div className="flex flex-col items-center gap-3 py-4 text-center">
        <CheckCircle2 className="h-10 w-10 text-green-500" />
        <p className="font-semibold">Payment successful!</p>
        {successMessage && (
          <p className="text-sm text-muted-foreground">{successMessage}</p>
        )}
      </div>
    );
  }

  return (
    <form id={formId} onSubmit={handleSubmit} className="space-y-3">
      <PaymentElement onReady={onReady} onLoadError={onLoadError} />
      {errorMessage && <p className="text-sm text-red-500">{errorMessage}</p>}
    </form>
  );
}
